/**
 * PillarCompliance — Roman numeral I, drift-trend hero, top drifts.
 *
 * Visual reference: 04-hybrid-recommended.html .pillar.compliance block.
 * Data sources:
 *   - DashboardStats              → score, drift trend, mini-grid counts.
 *   - usePriorityActions(compliance) → top items list (curated server-side
 *                                     ranking with Baseline-Compliance score
 *                                     lift estimates from BaselineDeltaCalculator).
 * Pending-commit copy still falls back when the queue is empty so the pillar
 * doesn't go silent on tenants whose active drifts have all been approved.
 */

import { useMemo } from 'react'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { useTheme } from '@/context/ThemeContext'
import { getThemeColors } from '@/lib/themeColors'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import {
  type ActivityItem,
  type DashboardStats,
} from '@/types'
import { ComplianceEmpty } from './EmptyStates'
import {
  Action,
  ActionsBlock,
  HeroMetric,
  MiniGrid,
  PillarShell,
} from './PillarShell'

interface Props {
  tenantId: string | undefined
  stats: DashboardStats | null | undefined
  isLoading: boolean
}

interface SparkPoint { date: string; value: number }
interface Trend { dir: 'up' | 'down' | 'flat'; label: string }

const RESOLVED_ACTIVITY_TYPES: ReadonlyArray<ActivityItem['type']> = [
  'drift_resolved',
  'drift_remediated',
]

function buildSpark(stats: DashboardStats | null | undefined): SparkPoint[] {
  return (stats?.driftTrends ?? []).map(t => ({
    date: t.date,
    value: (t.critical ?? 0) + (t.high ?? 0) + (t.medium ?? 0) + (t.low ?? 0) + (t.info ?? 0),
  }))
}

// Compliance trend is INVERTED: fewer drifts opened = positive movement, so
// a decrease shows as the "up" green pill, an increase shows as the red "down".
function computeComplianceTrend(spark: SparkPoint[]): Trend | null {
  if (spark.length < 8) return null
  const last7  = spark.slice(-7).reduce((sum, p) => sum + p.value, 0)
  const prior7 = spark.slice(-14, -7).reduce((sum, p) => sum + p.value, 0)
  if (last7 === 0 && prior7 === 0) return { dir: 'flat', label: 'flat' }
  if (prior7 === 0) return { dir: 'up', label: 'new' }

  const deltaPct = ((last7 - prior7) / prior7) * 100
  if (Math.abs(deltaPct) < 5) return { dir: 'flat', label: 'flat' }
  return deltaPct < 0
    ? { dir: 'up',   label: Math.abs(deltaPct).toFixed(1) }
    : { dir: 'down', label: deltaPct.toFixed(1) }
}

export default function PillarCompliance({ tenantId, stats, isLoading }: Props) {
  // Subscribe to theme so getThemeColors() picks up new vars on toggle.
  useTheme()
  const tc = getThemeColors()

  // Server-ranked drift-source priority actions. Cap at 3 to fit the pillar's
  // "Top items" slot. Pillar-filtered so cross-pillar items (BPA, identity)
  // never leak into the Compliance pillar's list.
  const { actions, isLoading: actionsLoading } = usePriorityActions({
    tenantId,
    maxCount: 3,
    pillar: 'compliance',
  })

  const { spark, active, pct, pendingCommits, resolved30d, critical, high, trend } = useMemo(() => {
    const spark = buildSpark(stats)
    const activity = stats?.recentActivity ?? []
    return {
      spark,
      active:  stats?.activeDrifts ?? 0,
      pct:     stats?.complianceScore?.percentage ?? null,
      pendingCommits: stats?.pendingProcessing ?? 0,
      resolved30d: activity.filter(a => RESOLVED_ACTIVITY_TYPES.includes(a.type)).length,
      critical: stats?.issuesBySeverity?.critical ?? 0,
      high:     stats?.issuesBySeverity?.high     ?? 0,
      trend: computeComplianceTrend(spark),
    }
  }, [stats])

  const isEmpty = !isLoading && spark.length === 0 && pct == null

  return (
    <PillarShell
      tone="compliance"
      roman="I"
      eyebrow="Baseline · Compliance"
      Icon={CheckCircleIcon}
      ctaLabel="Open compliance"
      ctaHref="/configuration-drift"
    >
      {isEmpty ? (
        <ComplianceEmpty ctaHref="/configuration-drift" />
      ) : (
        <>
          <HeroMetric value={pct} unit="%" isLoading={isLoading} trend={trend} />

          <p className="mt-2 mb-6 text-[13px] leading-[1.55] text-slate-400">
            Configurations holding to the agreed baseline.{' '}
            <strong className="font-bold text-slate-100 dark:text-slate-100">
              {active} active drift{active === 1 ? '' : 's'}
            </strong>
            .
          </p>

          <div className="mb-6 rounded-2xl bg-dark-800/60 px-4 py-3 dark:bg-white/[0.025]">
            <div className="mb-2 flex justify-between text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
              <span>30d trend</span>
              <span>drifts opened</span>
            </div>
            <div style={{ height: 56 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={spark.length ? spark : [{ date: '', value: 0 }]}>
                  <defs>
                    <linearGradient id="pillar-compliance-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={tc.primary} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={tc.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={tc.primary}
                    strokeWidth={2}
                    fill="url(#pillar-compliance-grad)"
                    fillOpacity={1}
                    isAnimationActive
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <MiniGrid
            cells={[
              { label: 'Active drifts',   value: String(active) },
              { label: 'Pending commits', value: String(pendingCommits) },
              { label: 'Resolved · 30d',  value: String(resolved30d) },
              { label: 'Critical · High', value: String(critical), suffix: `· ${high}` },
            ]}
          />

          <ActionsBlock label="Top items" allHref="/configuration-drift" ctaLabel="What's queued">
            {actionsLoading && actions.length === 0 ? (
              // Skeleton triplet so the items list shows movement while the
              // priority-actions fetch is in flight. Doesn't gate the rest of the pillar.
              <>
                <ActionSkeleton />
                <ActionSkeleton />
                <ActionSkeleton />
              </>
            ) : actions.length > 0 ? (
              actions.map(a => (
                <Action
                  key={a.id}
                  severity={a.severity}
                  title={a.title}
                  meta={a.meta}
                  guardianScoreDelta={a.guardianScoreDelta ?? null}
                  href={a.href}
                />
              ))
            ) : pendingCommits > 0 ? (
              <p className="py-3 text-[12px] leading-[1.55] text-slate-400">
                No drifts need your decision.{' '}
                <strong className="font-bold text-slate-100 dark:text-slate-100">
                  {pendingCommits}
                </strong>{' '}
                approved change{pendingCommits === 1 ? '' : 's'} ready to commit to baseline.
              </p>
            ) : (
              <p className="py-3 text-[12px] leading-[1.55] text-slate-400">
                Your baseline is steady — nothing needs your attention right now.
              </p>
            )}
          </ActionsBlock>
        </>
      )}
    </PillarShell>
  )
}

/** Skeleton row matching the Action layout proportions while priority-actions loads. */
function ActionSkeleton() {
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-dark-700 py-3 last:border-b-0">
      <span aria-hidden className="block h-8 w-1.5 rounded-[3px] bg-dark-700 animate-pulse" />
      <div className="min-w-0 space-y-1.5">
        <div className="h-3 w-3/4 rounded bg-dark-700 animate-pulse" />
        <div className="h-2.5 w-1/3 rounded bg-dark-700 animate-pulse" />
      </div>
      <span aria-hidden />
    </div>
  )
}
